import { Link, NavLink } from "react-router";
import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
} from "@/components/ui/sidebar";
import React from "react";
import AccessGuard from "@/components/access-guard";
import { ExternalLink } from "lucide-react";
import { useIsCustomer } from "@/hooks/useIsCustomer";
import { MaintenanceSidebarGroup } from "@/components/maintenance-sidebar-group";
import { DeviceLoadManagementSidebarGroup } from "@/components/device-load-management-sidebar-group";

export const ManageCustomersSidebar = ({
    ...props
}: React.ComponentProps<"div"> & {
    side?: "left" | "right";
    variant?: "sidebar" | "floating" | "inset";
    collapsible?: "offcanvas" | "icon" | "none";
}) => {
    const isCustomer = useIsCustomer();
    return (
        <Sidebar {...props}>
            {props.children}
            <SidebarContent>
                <SidebarGroup>
                    <SidebarGroupLabel>Customers</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <AccessGuard
                                permissions={[
                                    "device_group_create",
                                    "partner_organisation_manage",
                                ]}
                                permissionsSelector="any"
                                unauthorizedBehaviour={"hide"}
                            >
                                <SidebarMenuItem>
                                    <SidebarMenuButton asChild>
                                        <NavLink
                                            className={"text-default-color"}
                                            to={`/manage-customers/organisations`}
                                        >
                                            <span>Organisations</span>
                                        </NavLink>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </AccessGuard>

                            <SidebarMenuItem>
                                <SidebarMenuButton asChild>
                                    <NavLink
                                        className={"text-default-color"}
                                        to={`/manage-customers/sites`}
                                    >
                                        <span>Sites</span>
                                    </NavLink>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild>
                                    <NavLink
                                        className={"text-default-color"}
                                        to={`/manage-customers/devices`}
                                    >
                                        <span>Devices</span>
                                    </NavLink>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                            <AccessGuard
                                permissions={["system_device_provisioning"]}
                                unauthorizedBehaviour={"hide"}
                            >
                                <SidebarMenuItem>
                                    <SidebarMenuButton asChild>
                                        <NavLink
                                            className={"text-default-color"}
                                            to={`/manage-customers/device-owners`}
                                        >
                                            <span>Device owners</span>
                                        </NavLink>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </AccessGuard>
                            <AccessGuard
                                permissions={[
                                    "system_user_account_manage",
                                    "enterprise_user_account_read",
                                ]}
                                permissionsSelector="any"
                                unauthorizedBehaviour={"hide"}
                            >
                                <SidebarMenuItem>
                                    <SidebarMenuButton asChild>
                                        <NavLink
                                            className={"text-default-color"}
                                            to={`/manage-customers/users`}
                                            end
                                        >
                                            <span>Users</span>
                                        </NavLink>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </AccessGuard>
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild>
                                    <NavLink
                                        className={"text-default-color"}
                                        to={`/manage-customers/device-groups`}
                                    >
                                        <span>Device groups</span>
                                    </NavLink>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
                <DeviceLoadManagementSidebarGroup />
                {!isCustomer && (
                    <MaintenanceSidebarGroup basePath="/manage-customers" />
                )}

                <AccessGuard
                    permissions={[
                        "system_device_provisioning",
                        "partner_device_provisioning",
                        "device_provisioning",
                    ]}
                    permissionsSelector="any"
                    unauthorizedBehaviour={"hide"}
                >
                    <SidebarGroup>
                        <SidebarGroupLabel>Provisioning</SidebarGroupLabel>
                        <SidebarGroupContent>
                            <SidebarMenu>
                                <SidebarMenuItem>
                                    <SidebarMenuButton asChild>
                                        <Link
                                            className={"text-default-color"}
                                            to="https://jetcharge.atlassian.net/wiki/spaces/Training/pages/1665597449/Illuminate+On+Boarding+Steps+-+ChargeMate"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <span>
                                                Device onboarding guide{" "}
                                                <ExternalLink className="inline h-4 w-4 pb-0.5" />
                                            </span>
                                        </Link>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                </AccessGuard>
            </SidebarContent>
        </Sidebar>
    );
};
